/**
 * Title-only product matching (no fuzzy / no body/tags),
 * 5D-first ordering, out-of-stock last.
 */
(function (global) {
  if (global.FusmattenSearchSort) return;

  function normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function queryTokens(query) {
    return normalizeText(query).split(/\s+/).filter(function (token) {
      return token.length >= 2 || /^\d/.test(token);
    });
  }

  function hasWord(hay, token) {
    if (!token) return true;
    return (' ' + hay + ' ').indexOf(' ' + token + ' ') !== -1;
  }

  function titleMatchesQuery(title, query) {
    var tokens = queryTokens(query);
    if (!tokens.length) return false;
    var hay = normalizeText(title);
    if (!hay) return false;
    return tokens.every(function (token) {
      return hasWord(hay, token);
    });
  }

  function normalizeCode(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function codeMatchesQuery(codes, query) {
    var q = normalizeCode(query);
    if (q.length < 3) return false;
    var list = Array.isArray(codes) ? codes : String(codes || '').split('|');
    return list.some(function (code) {
      var c = normalizeCode(code);
      if (!c) return false;
      return c === q || (q.length >= 4 && c.indexOf(q) !== -1);
    });
  }

  function productMatchesQuery(title, codes, query) {
    return titleMatchesQuery(title, query) || codeMatchesQuery(codes, query);
  }

  function hasKofferraum(blob) {
    return /kofferr?aum/.test(blob);
  }

  function hasFloorMat(blob) {
    return /fussmatte|gummimatte|automatte|\bmatten\b/.test(blob.replace(/kofferr?aum\w*/g, ''));
  }

  function productTypeRank(title, tags) {
    var blob = normalizeText(title);
    if (hasKofferraum(blob) && !hasFloorMat(blob)) return 2;
    if (/\b5d\b/.test(blob)) return 0;
    if (/\b3d\b/.test(blob)) return 1;
    var tagBlob = normalizeText((tags || []).join(' '));
    if (/\b5d\b/.test(tagBlob)) return 0;
    if (/\b3d\b/.test(tagBlob)) return 1;
    return 3;
  }

  function productMatchesType(title, type) {
    if (!type) return true;
    var blob = normalizeText(title);
    if (type === 'Kofferraummatte') return hasKofferraum(blob) && !hasFloorMat(blob);
    var rank = productTypeRank(title, []);
    if (type === '5D') return rank === 0;
    if (type === '3D') return rank === 1;
    return true;
  }

  function productTypeKey(title, tags) {
    var rank = productTypeRank(title, tags);
    if (rank === 0) return '5D';
    if (rank === 1) return '3D';
    if (rank === 2) return 'Kofferraummatte';
    return '';
  }

  function parsePriceValue(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    var raw = String(value).trim();
    if (/^\d+$/.test(raw)) return parseInt(raw, 10);
    var cleaned = raw.replace(/[^\d,.\-]/g, '');
    if (cleaned.indexOf(',') !== -1 && cleaned.indexOf('.') !== -1) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (cleaned.indexOf(',') !== -1) {
      cleaned = cleaned.replace(',', '.');
    }
    var n = parseFloat(cleaned);
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  }

  function compareProducts(a, b) {
    var typeDiff = productTypeRank(a.title, a.tags) - productTypeRank(b.title, b.tags);
    if (typeDiff) return typeDiff;
    var aStock = a.available === false ? 1 : 0;
    var bStock = b.available === false ? 1 : 0;
    if (aStock !== bStock) return aStock - bStock;
    var priceDiff = parsePriceValue(b.price) - parsePriceValue(a.price);
    if (priceDiff) return priceDiff;
    return String(a.title || '').localeCompare(String(b.title || ''), undefined, { sensitivity: 'base' });
  }

  function scoreProduct(product, query) {
    var title = product.title || '';
    var hay = normalizeText(title);
    var q = normalizeText(query);
    var tokens = queryTokens(query);
    var rank = 0;

    if (q && hay === q) rank -= 300;
    else if (q && hasWord(hay, q) === false && hay.indexOf(q) !== -1) rank -= 80;
    else if (q && (' ' + hay + ' ').indexOf(' ' + q + ' ') !== -1) rank -= 180;

    if (tokens.length >= 2) {
      var phrase = tokens.join(' ');
      if ((' ' + hay + ' ').indexOf(' ' + phrase + ' ') !== -1) rank -= 160;
    }

    tokens.forEach(function (token) {
      if (hay.indexOf(token) === 0) rank -= 20;
    });

    rank += productTypeRank(title, product.tags) * 50;
    if (product.available === false) rank += 800;
    return rank;
  }

  function filterAndSortProducts(products, query) {
    var list = (products || []).slice();
    var q = String(query || '').trim();
    if (q) {
      var matched = list.filter(function (p) {
        var codes = (p.variants || []).map(function (v) { return v && v.sku; });
        return productMatchesQuery(p.title, codes, q);
      });
      if (matched.length) list = matched;
    }
    list.sort(compareProducts);
    return list;
  }

  function suggestUrl(query, limit) {
    var root = (global.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var url = new URL(root + 'search/suggest.json', global.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(limit || 50));
    url.searchParams.set('resources[options][fields]', 'title,variants.sku');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    return url.toString();
  }

  function sortDomCards(container, selector) {
    if (!container) return;
    // An explicit shopper sort (sort_by in the URL) wins over the default 5D-first ordering.
    if (new URLSearchParams(global.location.search).get('sort_by')) return;
    var cards = Array.prototype.slice.call(container.querySelectorAll(selector || ':scope > *'));
    if (!cards.length) return;

    cards.sort(function (a, b) {
      var aHidden = a.hidden || a.style.display === 'none';
      var bHidden = b.hidden || b.style.display === 'none';
      if (aHidden !== bHidden) return aHidden ? 1 : -1;

      var aTier = parseInt(a.getAttribute('data-sort-tier') || '9', 10);
      var bTier = parseInt(b.getAttribute('data-sort-tier') || '9', 10);
      if (aTier !== bTier) return aTier - bTier;

      var aStock = a.getAttribute('data-available') === 'false' ? 1 : 0;
      var bStock = b.getAttribute('data-available') === 'false' ? 1 : 0;
      if (aStock !== bStock) return aStock - bStock;

      var priceDiff = parsePriceValue(b.getAttribute('data-price')) - parsePriceValue(a.getAttribute('data-price'));
      if (priceDiff) return priceDiff;

      return (a.getAttribute('data-product-title') || '').localeCompare(
        b.getAttribute('data-product-title') || '',
        undefined,
        { sensitivity: 'base' }
      );
    });

    cards.forEach(function (card) {
      container.appendChild(card);
    });
  }

  function applyTitleFilterToCards(container, selector, query) {
    if (!container) return 0;
    var q = String(query || '').trim();
    var visible = 0;
    var cards = Array.prototype.slice.call(container.querySelectorAll(selector || ':scope > *'));
    cards.forEach(function (card) {
      var title = card.getAttribute('data-product-title') || '';
      var codes = card.getAttribute('data-product-skus') || '';
      var ok = !q || productMatchesQuery(title, codes, q);
      card.hidden = !ok;
      card.style.display = ok ? '' : 'none';
      if (ok) visible += 1;
    });
    if (q && visible === 0 && cards.length) {
      cards.forEach(function (card) {
        card.hidden = false;
        card.style.display = '';
      });
      visible = cards.length;
    }
    sortDomCards(container, selector);
    return visible;
  }

  var TYPE_GRID_SELECTOR = '#AdvProductGrid, results-list .product-grid';

  function cardTitle(card) {
    if (card.hasAttribute('data-product-title')) return card.getAttribute('data-product-title');
    var inner = card.querySelector('[data-product-title]');
    return inner ? inner.getAttribute('data-product-title') : null;
  }

  function ensureTypeFilterStyles() {
    if (document.getElementById('fm-type-filter-style')) return;
    var style = document.createElement('style');
    style.id = 'fm-type-filter-style';
    style.textContent = '.fm-type-hidden{display:none!important}.fm-type-empty{grid-column:1/-1;text-align:center;padding:40px 16px;color:#666;font-size:15px}';
    document.head.appendChild(style);
  }

  var TAB_TYPES = ['5D', '3D', 'Kofferraummatte'];

  // Hides type tabs without products on the page; hides the whole bar when fewer than two types exist.
  function syncTypeTabVisibility() {
    var grids = document.querySelectorAll(TYPE_GRID_SELECTOR);
    var available = {};
    if (!grids.length) return null;
    // Partially loaded grids can't prove a type is missing from the whole collection.
    var complete = Array.prototype.every.call(grids, function (g) { return g.getAttribute('data-all-loaded') === 'true'; });
    if (!complete) {
      document.querySelectorAll('[data-product-type-tabs][data-tab-mode="form"]').forEach(function (root) {
        root.hidden = false;
        root.querySelectorAll('[data-type]').forEach(function (btn) { btn.hidden = false; });
      });
      return null;
    }
    grids.forEach(function (grid) {
      Array.prototype.slice.call(grid.children).forEach(function (card) {
        var title = cardTitle(card);
        if (title == null) return;
        TAB_TYPES.forEach(function (t) { if (productMatchesType(title, t)) available[t] = true; });
      });
    });
    var count = Object.keys(available).length;
    document.querySelectorAll('[data-product-type-tabs][data-tab-mode="form"]').forEach(function (root) {
      TAB_TYPES.forEach(function (t) {
        root.querySelectorAll('[data-type="' + t + '"]').forEach(function (btn) { btn.hidden = !available[t]; });
      });
      root.hidden = count < 2;
    });
    return available;
  }

  function applyTypeFilterToGrids(type) {
    ensureTypeFilterStyles();
    var available = syncTypeTabVisibility();
    if (type && available && (!available[type] || Object.keys(available).length < 2)) {
      type = '';
      global.__fusmattenProductType = '';
      try { localStorage.setItem('fusmatten-product-type', ''); } catch (e) {}
      document.querySelectorAll('[data-product-type-tabs] [data-type]').forEach(function (btn) {
        var on = (btn.getAttribute('data-type') || '') === '';
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }
    document.querySelectorAll(TYPE_GRID_SELECTOR).forEach(function (grid) {
      var visible = 0;
      var total = 0;
      Array.prototype.slice.call(grid.children).forEach(function (card) {
        if (card.classList.contains('fm-type-empty')) return;
        var title = cardTitle(card);
        if (title == null) return;
        total += 1;
        var ok = productMatchesType(title, type);
        card.classList.toggle('fm-type-hidden', !ok);
        if (ok && !card.hidden && card.style.display !== 'none') visible += 1;
      });
      var empty = grid.querySelector(':scope > .fm-type-empty');
      if (type && total && !visible) {
        if (!empty) {
          empty = document.createElement('div');
          empty.className = 'fm-type-empty';
          grid.appendChild(empty);
        }
        empty.textContent = 'Für diese Auswahl sind auf dieser Seite keine Produkte dieses Typs vorhanden.';
      } else if (empty) {
        empty.remove();
      }
    });
  }

  function initTypeFilter() {
    var current = global.__fusmattenProductType;
    if (current == null) {
      try { current = localStorage.getItem('fusmatten-product-type') || ''; } catch (e) { current = ''; }
    }
    applyTypeFilterToGrids(current);
    document.addEventListener('fusmatten:product-type', function (event) {
      current = (event.detail && event.detail.type) || '';
      applyTypeFilterToGrids(current);
    });
    if (typeof MutationObserver === 'function') {
      document.querySelectorAll(TYPE_GRID_SELECTOR).forEach(function (grid) {
        var pending = false;
        new MutationObserver(function (mutations) {
          var added = mutations.some(function (m) {
            return Array.prototype.some.call(m.addedNodes, function (n) {
              return n.nodeType === 1 && !n.classList.contains('fm-type-empty');
            });
          });
          if (!added || pending) return;
          pending = true;
          requestAnimationFrame(function () {
            pending = false;
            applyTypeFilterToGrids(current);
          });
        }).observe(grid, { childList: true });
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initTypeFilter);
  else initTypeFilter();

  global.FusmattenSearchSort = {
    normalizeText: normalizeText,
    titleMatchesQuery: titleMatchesQuery,
    codeMatchesQuery: codeMatchesQuery,
    productMatchesQuery: productMatchesQuery,
    filterAndSortProducts: filterAndSortProducts,
    sortDomCards: sortDomCards,
    productTypeRank: productTypeRank,
    productTypeKey: productTypeKey,
    productMatchesType: productMatchesType,
    parsePriceValue: parsePriceValue,
    compareProducts: compareProducts,
    suggestUrl: suggestUrl,
    applyTitleFilterToCards: applyTitleFilterToCards
  };
})(window);
